import { useEffect, useState } from 'react';

export interface AssetDimensions {
  width: number;
  height: number;
}

// The asset API does not return image dimensions, so they are read from the decoded image
// and cached across mounts to avoid re-downloading on every dialog open.
const dimensionsCache = new Map<string, AssetDimensions | null>();

export const useAssetDimensions = (url?: string, enabled = true): AssetDimensions | null => {
  const [dimensions, setDimensions] = useState<AssetDimensions | null>(() =>
    url ? (dimensionsCache.get(url) ?? null) : null,
  );

  useEffect(() => {
    if (!enabled || !url) {
      setDimensions(null);
      return;
    }

    const cached = dimensionsCache.get(url);
    if (cached !== undefined) {
      setDimensions(cached);
      return;
    }

    let isCancelled = false;
    const image = new Image();

    image.onload = () => {
      const value = { width: image.naturalWidth, height: image.naturalHeight };
      dimensionsCache.set(url, value);
      if (!isCancelled) setDimensions(value);
    };

    image.onerror = () => {
      dimensionsCache.set(url, null);
      if (!isCancelled) setDimensions(null);
    };

    image.src = url;

    return () => {
      isCancelled = true;
      image.onload = null;
      image.onerror = null;
    };
  }, [url, enabled]);

  return dimensions;
};
